import { useTheme } from "@emotion/react";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField as MuiTextField,
  MenuItem,
  Select,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Stack,
  Chip,
  Switch,
} from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import Headeer from "../../components/Headeer";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import FormService from "../../services/doctorServices/FormService";
import WarningSnackbar from "../../components/SnackbarComponent";
import FileService from "../../services/doctorServices/FileService";
import FormulaService from "../../services/doctorServices/FormulaService";
import AlertDialog from "../../components/AlertDialog";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TableChartIcon from "@mui/icons-material/TableChart";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ImageIcon from "@mui/icons-material/Image";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import ImageQuestion from "../../components/ImageQuestion";
import RepeatSchedulePicker from "../../components/RepeatSchedulePicker";
import RatingScaleQuestionEditor from "../../components/RatingScaleQuestionEditor";

/** Çok sütunda yatay taşmayı önlemek: sabit tablo + eşit yüzde sütun genişliği */
function tableEqualColumnWidthPct(totalColumns) {
  const raw = Number(totalColumns);
  const n = Number.isFinite(raw) && raw >= 1 ? raw : 1;
  return `${100 / n}%`;
}

function tableCellShrinkBase(totalColumns, extra = {}) {
  return {
    minWidth: 0,
    width: tableEqualColumnWidthPct(totalColumns),
    verticalAlign: "top",
    wordBreak: "break-word",
    boxSizing: "border-box",
    ...extra,
  };
}

/**
 * getFormInfo / API soru listesini createForm içindeki `fields` yapısına çevirir (form kopyası ön doldurma).
 */
function mapApiQuestionsToCreateFormFields(apiQuestions) {
  if (!Array.isArray(apiQuestions)) return [];
  return apiQuestions.map((q) => {
    const base = {
      type: q.type,
      question: q.question ?? "",
      category: q.category ?? "",
      hasImportance: q.hasImportance !== undefined ? q.hasImportance : false,
    };
    if (q.type === "multiple_choice") {
      return {
        ...base,
        options: (q.options || []).map((opt) => ({
          label: opt.option ?? opt.label ?? opt.value ?? "",
          importance:
            opt.option_level != null && opt.option_level !== ""
              ? opt.option_level
              : null,
        })),
      };
    }
    if (q.type === "table") {
      const headers = [...(q.tableHeaders || [])];
      const importance = [...(q.tableHeaderImportance || [])];
      while (importance.length < headers.length) importance.push(0);
      return {
        ...base,
        tableHeaders: headers,
        tableQuestions: [...(q.tableQuestions || [])],
        tableRows: q.tableRows ?? (q.tableQuestions?.length || 2),
        tableCols: q.tableCols ?? (headers.length || 2),
        tableHeaderImportance: importance.slice(0, headers.length),
      };
    }
    if (q.type === "image") {
      return {
        ...base,
        imageUrl: q.imageUrl || "",
        imageAnswerMode: q.imageAnswerMode || "select",
        areas: Array.isArray(q.areas)
          ? q.areas.map((a) => ({ ...a }))
          : [],
      };
    }
    if (q.type === "rating_scale") {
      const d = Array.isArray(q.scaleImportances) ? [...q.scaleImportances] : [];
      while (d.length < 11) d.push(d.length);
      if (d.length > 11) d.length = 11;
      return {
        ...base,
        minLabel: q.minLabel ?? "",
        maxLabel: q.maxLabel ?? "",
        positiveEnd: q.positiveEnd === "max" ? "max" : "min",
        scaleImportances: d.map((n, i) => {
          const x = Number(n);
          return Number.isNaN(x) ? i : x;
        }),
      };
    }
    return base;
  });
}

export default function Index() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation(['forms', 'common']);
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const formService = new FormService();
  const fileService = new FileService();
  const formulaService = new FormulaService();
  const [title, setTitle] = useState(t('forms:untitledForm'));
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState([]);
  const [forPatients, setForPatients] = useState("");
  const navigate = useNavigate();
  const [snackbarState, setSnackbarState] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState({ open: false, message: "" });
  const [copyDraftSnackbar, setCopyDraftSnackbar] = useState({
    open: false,
    message: "",
  });
  const [fileInfo, setFileInfo] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [selectedFormulaId, setSelectedFormulaId] = useState("");
  
  // Table Question Modal States
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [tableRows, setTableRows] = useState(2);
  const [tableCols, setTableCols] = useState(2);
  const [tableHeaders, setTableHeaders] = useState(["Option 1", "Option 2"]);
  const [tableQuestions, setTableQuestions] = useState(["Question 1", "Question 2"]);
  const [tableHeaderImportance, setTableHeaderImportance] = useState([0, 0]); // Her header için importance
  
  // Alert Dialog State
  const [noFormulaConfirmDialogOpen, setNoFormulaConfirmDialogOpen] = useState(false);
  
  // Get form type from URL parameter and set it
  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam) {
      // Decode URL parameter (for%20patients -> for patients, for%20me -> for me)
      const decodedType = decodeURIComponent(typeParam);
      if (decodedType === "for patients" || decodedType === "for me") {
        setForPatients(decodedType);
        if (decodedType === "for patients") {
          setRepeatCount((prev) => {
            const n = parseInt(prev, 10);
            if (prev === "" || prev == null || Number.isNaN(n) || n < 1) return "1";
            return prev;
          });
        }
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const getFileInfo = async () => {
      try {
        const response = await fileService.getFileInfo(id);
        setFileInfo(response);
      } catch (err) {
        console.error("Error fetching file information:", err);
      }
    };
    if (id) {
      getFileInfo();
    }
  }, [id]);

  useEffect(() => {
    const getFormulas = async () => {
      try {
        const response = await formulaService.getAllFormulas();
        setFormulas(response);
      } catch (err) {
        console.error("Error fetching formulas:", err);
      }
    };
    getFormulas();
  }, []);

  const capitalizeWords = (str) => {
    return str.trim().replace(/\b\w/g, char => char.toUpperCase());
  };

  const handleTitleBlur = () => {
    if (title && title.trim()) {
      const capitalizedTitle = capitalizeWords(title);
      setTitle(capitalizedTitle);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Question validation
    if (fields.length === 0) {
      setErrorSnackbar({
        open: true,
        message: t('forms:pleaseAddAtLeastOneQuestion')
      });
      return;
    }

    // Form type validation (should come from URL parameter)
    if (!forPatients) {
      setErrorSnackbar({
        open: true,
        message: t('forms:formTypeRequired')
      });
      return;
    }

    // Formula selection validation (OPTIONAL - ask for confirmation)
    if (!selectedFormulaId) {
      setNoFormulaConfirmDialogOpen(true);
      return;
    }

    // Repeat schedule validation - Only for "for patients"
    if (forPatients === "for patients") {
    const count = parseInt(repeatCount) || 0;
      if (count > 1) {
      if (!repeatSchedule || repeatSchedule.length !== count - 1) {
        setErrorSnackbar({
          open: true,
          message: t('forms:pleaseConfigureRepeatSchedule', { count: count - 1, total: count })
        });
        return;
      }
      
      // Validate that all schedule items are filled
      const hasEmptyItems = repeatSchedule.some(item => !item || item === "");
      if (hasEmptyItems) {
        setErrorSnackbar({
          open: true,
          message: t('forms:pleaseFillInAllScheduleItems')
        });
        return;
        }
      }
    }

    // If formula is selected, create directly
    createFormWithData();
  };

  const createFormWithData = () => {
    // Capitalize title before submitting
    const capitalizedTitle = capitalizeWords(title);
    
    const data = {
      name: capitalizedTitle,
      description: description,
      questions: fields.map((field) => {
        const questionData = {
          question: field.question,
          type: field.type,
          category: field.category || null, // Category added (optional)
          hasImportance: field.hasImportance !== undefined ? field.hasImportance : false, // Include importance flag
        };
        if (field.type === "multiple_choice") {
          questionData.options = field.options.map((opt) => ({
            option: opt.label,
            option_level: opt.importance,
          }));
        }
        if (field.type === "table") {
          questionData.tableHeaders = field.tableHeaders;
          questionData.tableQuestions = field.tableQuestions;
          questionData.tableRows = field.tableRows;
          questionData.tableCols = field.tableCols;
          questionData.tableHeaderImportance = field.tableHeaderImportance; // Add importance values
        }
        if (field.type === "image") {
          questionData.imageUrl = field.imageUrl;
          questionData.imageAnswerMode = field.imageAnswerMode || "select";
          questionData.areas = field.areas;
        }
        if (field.type === "rating_scale") {
          questionData.minLabel = field.minLabel || "";
          questionData.maxLabel = field.maxLabel || "";
          questionData.positiveEnd = field.positiveEnd === "max" ? "max" : "min";
          const arr = Array.isArray(field.scaleImportances)
            ? field.scaleImportances
            : [];
          const filled = [];
          for (let i = 0; i <= 10; i++) {
            const x = Number(arr[i]);
            filled[i] = Number.isNaN(x) ? i : x;
          }
          questionData.scaleImportances = filled;
        }
        return questionData;
      }),
      type: forPatients,
      file_id: id,
      scoring_formula_id: selectedFormulaId || null, // Send null instead of empty string
      // Send repeat count and schedule only for "for patients"
      repeat_count: forPatients === "for patients" 
        ? (repeatCount && parseInt(repeatCount) > 0 ? parseInt(repeatCount) : 1)
        : 1,
      repeat_schedule: forPatients === "for patients"
        ? (repeatSchedule && repeatSchedule.length > 0 ? repeatSchedule : null)
        : null,
    };
    
    const handleCreateForm = async (data) => {
      setIsSubmitting(true);
      try {
        const response = await formService.createForm(data);
        setSnackbarState(true);
        setTitle(t('forms:untitledForm'));
        setDescription("");
        setFields([]);
        setSelectedFormulaId(""); // Reset formula selection
        setRepeatCount("1"); // Reset — varsayılan tek gönderim
        setRepeatSchedule([]); // Reset repeat_schedule
        setScheduleDialogOpen(false); // Close dialog
        setExcludeAllFromScoring(false); // Reset global scoring exclusion
        setTimeout(() => {
          navigate("/files");
        }, 1000);
      } catch (err) {
        // 403 (Permission) errors are handled by global interceptor
        // Don't show alert for permission errors
        if (err.response?.status === 403) {
          // Global permission alert will be shown automatically
          return;
        }
        
        // Show error snackbar for other errors
        let errorMessage = t('forms:errorCreatingForm');
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setErrorSnackbar({
          open: true,
          message: errorMessage
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    handleCreateForm(data);
  };

  const handleAddTextQuestion = () => {
    const newField = {
      type: "text",
      question: "",
      category: "", // Category field added
      hasImportance: excludeAllFromScoring ? false : false, // Text questions don't have importance by default
    };
    setFields([...fields, newField]);
  };

  const handleAddMultipleChoiceQuestion = () => {
    const newField = {
      type: "multiple_choice",
      options: [
        { label: "1", importance: null },
        { label: "2", importance: null },
        { label: "3", importance: null },
      ],
      question: "",
      category: "", // Category field added
      hasImportance: excludeAllFromScoring ? false : true, // Multiple choice questions have importance by default (unless global exclusion is on)
    };
    setFields([...fields, newField]);
  };

  const handleAddImageQuestion = () => {
    const newField = {
      type: "image",
      question: t('forms:imageQuestionDefaultText'),
      imageUrl: "",
      imageAnswerMode: "select",
      areas: [],
      category: "", // Category field added
      hasImportance: excludeAllFromScoring ? false : false, // Image questions don't have importance by default
    };
    setFields([...fields, newField]);
  };

  const handleAddRatingScaleQuestion = () => {
    const newField = {
      type: "rating_scale",
      question: "",
      category: "",
      hasImportance: excludeAllFromScoring ? false : true,
      minLabel: "",
      maxLabel: "",
      positiveEnd: "min",
      scaleImportances: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    };
    setFields([...fields, newField]);
  };

  const handleQuestionChange = (index, value) => {
    const updatedFields = [...fields];
    updatedFields[index].question = value;
    setFields(updatedFields);
  };

  const handleAddOption = (index) => {
    const updatedFields = [...fields];
    updatedFields[index].options.push({ label: "", importance: null });
    setFields(updatedFields);
  };

  const handleOptionChange = (fieldIndex, optionIndex, updatedOption) => {
    const updatedFields = [...fields];
    updatedFields[fieldIndex].options[optionIndex] = updatedOption;
    setFields(updatedFields);
  };

  const handleImageQuestionDataChange = (index, updatedData) => {
    const updatedFields = [...fields];
    updatedFields[index] = {
      ...updatedFields[index],
      ...updatedData
    };
    setFields(updatedFields);
  };

  const handleDeleteQuestion = (index) => {
    const updatedFields = [...fields];
    updatedFields.splice(index, 1);
    setFields(updatedFields);
  };
  const handleCloseSnackBar = () => {
    setSnackbarState(false);
  };
  const handleCloseErrorSnackbar = () => {
    setErrorSnackbar({ open: false, message: "" });
  };
  const handleCloseCopyDraftSnackbar = () => {
    setCopyDraftSnackbar({ open: false, message: "" });
  };
  const [repeatCount, setRepeatCount] = useState("1"); // Toplam tekrar; hasta formunda boş bırakılmasın diye varsayılan 1
  const [repeatSchedule, setRepeatSchedule] = useState([]); // Repeat schedule array
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false); // Dialog state
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submit
  const [excludeAllFromScoring, setExcludeAllFromScoring] = useState(false); // Global flag to exclude all questions from scoring

  // FormInfo'dan "kopyala" ile gelen taslak — API'ye yazmadan önce düzenleme
  useEffect(() => {
    const draft = location.state?.copyFormDraft;
    if (!id || !draft) return;

    setTitle(draft.name?.trim() || t("forms:untitledForm"));
    setDescription(draft.description || "");

    const formType =
      draft.type === "for me" || draft.type === "for patients"
        ? draft.type
        : "for patients";
    setForPatients(formType);

    const mapped = mapApiQuestionsToCreateFormFields(draft.questions || []);
    setFields(mapped);

    if (draft.scoring_formula_id) {
      setSelectedFormulaId(draft.scoring_formula_id);
    } else {
      setSelectedFormulaId("");
    }

    if (formType === "for patients") {
      const rc =
        draft.repeat_count != null && draft.repeat_count !== ""
          ? String(draft.repeat_count)
          : "1";
      setRepeatCount(rc);
      setRepeatSchedule(
        Array.isArray(draft.repeat_schedule) ? [...draft.repeat_schedule] : []
      );
    } else {
      setRepeatCount("1");
      setRepeatSchedule([]);
    }

    const allExcluded =
      mapped.length > 0 && mapped.every((f) => f.hasImportance === false);
    setExcludeAllFromScoring(allExcluded);

    const qs = searchParams.toString();
    const path = qs ? `/Create-NewForm/${id}?${qs}` : `/Create-NewForm/${id}`;
    navigate(path, { replace: true, state: {} });

    setCopyDraftSnackbar({
      open: true,
      message: t("forms:copyFormDraftLoaded"),
    });
  }, [id, location.state, navigate, searchParams, t]);

  const handleMatchLabelImportance = (questionIndex) => {
    const updatedFields = [...fields];
    updatedFields[questionIndex].options = updatedFields[
      questionIndex
    ].options.map((opt) => ({
      ...opt,
      importance: isNaN(Number(opt.label)) ? opt.importance : Number(opt.label),
    }));
    setFields(updatedFields);
  };

  const handleDuplicateQuestion = (index) => {
    const updatedFields = [...fields];
    const copiedField = { ...updatedFields[index] }; // Copy the question
    // Deep clone options if it's multiple_choice
    if (copiedField.type === "multiple_choice") {
      copiedField.options = copiedField.options.map((opt) => ({ ...opt }));
    }
    // Deep clone table data if it's table
    if (copiedField.type === "table") {
      copiedField.tableHeaders = [...copiedField.tableHeaders];
      copiedField.tableQuestions = [...copiedField.tableQuestions];
      copiedField.tableHeaderImportance = [...copiedField.tableHeaderImportance];
    }
    // Deep clone image data if it's image
    if (copiedField.type === "image") {
      copiedField.imageAnswerMode = copiedField.imageAnswerMode || "select";
      copiedField.areas = (copiedField.areas || []).map(area => ({ ...area }));
    }
    if (copiedField.type === "rating_scale") {
      copiedField.scaleImportances = [...(copiedField.scaleImportances || [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10])];
      copiedField.minLabel = copiedField.minLabel || "";
      copiedField.maxLabel = copiedField.maxLabel || "";
      copiedField.positiveEnd = copiedField.positiveEnd === "max" ? "max" : "min";
      delete copiedField._previewValue;
    }
    // Copy category
    copiedField.category = copiedField.category || "";
    // Copy hasImportance
    copiedField.hasImportance = copiedField.hasImportance !== undefined ? copiedField.hasImportance : false;
    updatedFields.splice(index + 1, 0, copiedField); // Add below the same question
    setFields(updatedFields);
  };

  // Table Question Functions
  const handleAddTableQuestion = () => {
    setTableModalOpen(true);
    // Reset to default values
    // setTableRows(2);
    setTableCols(2);
    setTableHeaders([t('forms:optionNumber', { index: 1 }), t('forms:optionNumber', { index: 2 })]);
    setTableQuestions([t('forms:questionNumber', { index: 1 }), t('forms:questionNumber', { index: 2 })]);
    setTableHeaderImportance([0, 0]); // Reset importance values
  };

  const handleTableRowsChange = (newRows) => {
    if (newRows >= 2 && newRows <= 20) {
      setTableRows(newRows);
      // Update questions array
      const newQuestions = [...tableQuestions];
      if (newRows > tableQuestions.length) {
        // Add new questions
        for (let i = tableQuestions.length; i < newRows; i++) {
          newQuestions.push(t('forms:questionNumber', { index: i + 1 }));
        }
      } else {
        // Remove excess questions
        newQuestions.splice(newRows);
      }
      setTableQuestions(newQuestions);
    }
  };

  const handleTableColsChange = (newCols) => {
    if (newCols >= 2 && newCols <= 15) {
      setTableCols(newCols);
      // Update headers array
      const newHeaders = [...tableHeaders];
      const newImportance = [...tableHeaderImportance];
      
      if (newCols > tableHeaders.length) {
        // Add new headers and importance values
        for (let i = tableHeaders.length; i < newCols; i++) {
          newHeaders.push(t('forms:optionNumber', { index: i + 1 }));
          newImportance.push(0);
        }
      } else {
        // Remove excess headers and importance values
        newHeaders.splice(newCols);
        newImportance.splice(newCols);
      }
      setTableHeaders(newHeaders);
      setTableHeaderImportance(newImportance);
    }
  };

  const handleTableHeaderChange = (index, value) => {
    const newHeaders = [...tableHeaders];
    newHeaders[index] = value;
    setTableHeaders(newHeaders);
  };

  const handleTableQuestionChange = (index, value) => {
    const newQuestions = [...tableQuestions];
    newQuestions[index] = value;
    setTableQuestions(newQuestions);
  };

  const handleTableHeaderImportanceChange = (index, value) => {
    const newImportance = [...tableHeaderImportance];
    // +/- butonları sayı gönderir; yazı alanı string — baştaki 0 ile "010" oluşmasını önle
    if (typeof value === "number" && !Number.isNaN(value)) {
      newImportance[index] = Math.max(0, Math.floor(value));
    } else {
      const digitsOnly = String(value).replace(/\D/g, "");
      if (digitsOnly === "") {
        newImportance[index] = 0;
      } else {
        const n = parseInt(digitsOnly, 10);
        newImportance[index] = Number.isNaN(n) ? 0 : Math.max(0, n);
      }
    }
    setTableHeaderImportance(newImportance);
  };

  const handleCreateTableQuestion = () => {
    const newField = {
      type: "table",
      question: t('forms:tableQuestion'),
      tableRows: tableRows,
      tableCols: tableCols,
      tableHeaders: [...tableHeaders],
      tableQuestions: [...tableQuestions],
      tableHeaderImportance: [...tableHeaderImportance], // Add importance values
      category: "", // Category field added
      hasImportance: excludeAllFromScoring ? false : true, // Table questions have importance by default (unless global exclusion is on)
    };
    setFields([...fields, newField]);
    setTableModalOpen(false);
  };

  const questionMeta = useMemo(() => ({
    text: {
      title: t('forms:freeTextQuestion'),
      subtitle: t('forms:collectOpenEndedResponses'),
      gradient: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[400]} 100%)`,
      icon: <TextFieldsOutlinedIcon />,
    },
    multiple_choice: {
      title: t('forms:multipleChoiceQuestion'),
      subtitle: t('forms:configureAnswerOptions'),
      gradient: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[500]} 100%)`,
      icon: <FormatListBulletedOutlinedIcon />,
    },
    table: {
      title: t('forms:tableQuestion'),
      subtitle: t('forms:scoreAnswersAcrossMatrix'),
      gradient: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
      icon: <TableChartIcon />,
    },
    image: {
      title: t('forms:imageQuestionICRS'),
      subtitle: t('forms:markAnatomicalRegions'),
      gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
      icon: <ImageIcon />,
    },
    rating_scale: {
      title: t('forms:ratingScaleQuestion'),
      subtitle: t('forms:ratingScaleSubtitle'),
      gradient: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
      icon: <LinearScaleIcon />,
    },
  }), [t, colors]);

  return (
    <Box sx={{ padding: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Headeer title={t('forms:createForm')} subtitle={fileInfo.name} />

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
          {/* Repeat Count (Kaç Kere) - Only show for "for patients" */}
          {forPatients === "for patients" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: -3.5 }}>
            <Typography
              variant="caption"
              sx={{ 
                color: colors.grey[300], 
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                fontWeight: 700
              }}
            >
              🔢 {t('forms:totalRepetitions')}
            </Typography>
            
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() => {
                  const currentValue = parseInt(repeatCount) || 0;
                  if (currentValue > 1) {
                    setRepeatCount((currentValue - 1).toString());
                  }
                }}
                disabled={!repeatCount || parseInt(repeatCount) <= 1}
                size="small"
                sx={{
                  backgroundColor: colors.redAccent[600],
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: colors.redAccent[700],
                    transform: "scale(1.1)",
                    boxShadow: `0 4px 12px rgba(219, 79, 74, 0.4)`
                  },
                  "&:disabled": {
                    backgroundColor: colors.grey[700],
                    color: colors.grey[500],
                    transform: "scale(1)",
                    boxShadow: "none"
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: `2px solid ${colors.redAccent[500]}`,
                  "&:hover:not(:disabled)": {
                    border: `2px solid ${colors.redAccent[400]}`
                  }
                }}
              >
                <RemoveIcon sx={{ fontSize: 18 }} />
              </IconButton>
              
              <TextField
                type="number"
                value={repeatCount}
                onChange={(e) => {
                  const value = e.target.value;
                  // 0 veya negatif değer girilmesini engelle
                  if (value === "" || (parseInt(value) >= 1 && parseInt(value) <= 999)) {
                    setRepeatCount(value);
                  }
                }}
                onBlur={() => {
                  const n = parseInt(String(repeatCount).trim(), 10);
                  if (
                    repeatCount === "" ||
                    String(repeatCount).trim() === "" ||
                    Number.isNaN(n) ||
                    n < 1
                  ) {
                    setRepeatCount("1");
                  }
                }}
                placeholder="1"
                variant="outlined"
                size="small"
                inputProps={{
                  min: 1,
                  max: 999,
                  style: { textAlign: "center" }
                }}
                sx={{
                  width: 80,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    textAlign: "center",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    backgroundColor: colors.primary[600],
                    border: `2px solid ${colors.greenAccent[600]}`,
                    "&:hover": {
                      border: `2px solid ${colors.greenAccent[500]}`,
                      backgroundColor: colors.primary[500]
                    },
                    "&.Mui-focused": {
                      border: `2px solid ${colors.greenAccent[400]}`,
                      backgroundColor: colors.primary[400],
                      boxShadow: `0 0 0 3px ${colors.greenAccent[900]}`
                    }
                  },
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                    color: colors.grey[100],
                    fontWeight: "bold"
                  },
                  "& input[type=number]": {
                    "-moz-appearance": "textfield"
                  },
                  "& input[type=number]::-webkit-outer-spin-button": {
                    "-webkit-appearance": "none",
                    margin: 0
                  },
                  "& input[type=number]::-webkit-inner-spin-button": {
                    "-webkit-appearance": "none",
                    margin: 0
                  }
                }}
              />
              
              <IconButton
                onClick={() => {
                  const currentValue = parseInt(repeatCount) || 0;
                  if (currentValue < 999) {
                    setRepeatCount((currentValue + 1).toString());
                  }
                }}
                disabled={parseInt(repeatCount) >= 999}
                size="small"
                sx={{
                  backgroundColor: colors.greenAccent[600],
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: colors.greenAccent[700],
                    transform: "scale(1.1)",
                    boxShadow: `0 4px 12px rgba(76, 206, 172, 0.4)`
                  },
                  "&:disabled": {
                    backgroundColor: colors.grey[700],
                    color: colors.grey[500],
                    transform: "scale(1)",
                    boxShadow: "none"
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: `2px solid ${colors.greenAccent[500]}`,
                  "&:hover:not(:disabled)": {
                    border: `2px solid ${colors.greenAccent[400]}`
                  }
                }}
              >
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton>
            
            {/* Configure Schedule Button - Shows when repeatCount > 1 */}
            {parseInt(repeatCount) > 1 && (
              <Button
                variant="outlined"
                onClick={() => setScheduleDialogOpen(true)}
                sx={{
                  ml: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  borderColor: colors.blueAccent[500],
                  color: colors.blueAccent[400],
                  backgroundColor: colors.primary[600],
                  "&:hover": {
                    borderColor: colors.blueAccent[400],
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                  },
                }}
              >
                ⚙️ {t('forms:configureSchedule')}
              </Button>
            )}
          </Box>
        </Box>
          )}

          {/* Scoring Formula Selection */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: -3.5 }}>
            <Typography
              variant="caption"
              sx={{ 
                color: colors.grey[300], 
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 0.5
              }}
            >
              {t('forms:scoringFormulaOptional')}
            </Typography>
            <FormControl
              sx={{
                minWidth: 250,
                maxWidth: { xs: "100%", sm: 420 },
                width: "100%",
                borderRadius: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: colors.primary[600],
                  border: selectedFormulaId 
                    ? `2px solid ${colors.greenAccent[600]}` 
                      : `2px solid ${colors.grey[700]}`,
                  "&:hover": {
                    border: selectedFormulaId
                      ? `2px solid ${colors.greenAccent[500]}`
                        : `2px solid ${colors.grey[600]}`,
                    backgroundColor: colors.primary[500],
                  },
                  "&.Mui-focused": {
                    border: `2px solid ${colors.blueAccent[400]}`,
                    backgroundColor: colors.primary[400],
                    boxShadow: `0 0 0 3px ${colors.blueAccent[900]}`,
                  },
                },
              }}
            >
              <Select
                value={selectedFormulaId}
                onChange={(e) => setSelectedFormulaId(e.target.value)}
                displayEmpty
                IconComponent={ExpandMoreIcon}
                renderValue={(value) => {
                  if (value === "" || value == null) {
                    return <em>{t("forms:noFormulaOptional")}</em>;
                  }
                  const f = formulas.find(
                    (x) => String(x.id) === String(value)
                  );
                  return f ? (
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        maxWidth: "100%",
                      }}
                    >
                      {f.name}
                    </Typography>
                  ) : (
                    ""
                  );
                }}
                sx={{
                  borderRadius: 2,
                  "& .MuiSelect-select": {
                    padding: "10px 14px",
                    color: selectedFormulaId ? colors.grey[100] : colors.grey[400],
                    fontWeight: selectedFormulaId ? 600 : 500,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                      mt: 1,
                      maxHeight: 300,
                      maxWidth: { xs: "calc(100vw - 24px)", sm: 420 },
                      backgroundColor: colors.primary[400],
                      "& .MuiMenuItem-root": {
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        maxWidth: "100%",
                        "&:hover": {
                          backgroundColor: colors.blueAccent[700],
                          color: "white",
                        },
                        "&.Mui-selected": {
                          backgroundColor: colors.blueAccent[800],
                          "&:hover": {
                            backgroundColor: colors.blueAccent[700],
                          },
                        },
                      },
                    },
                  },
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                }}
              >
                <MenuItem value="">
                  <em>{t('forms:noFormulaOptional')}</em>
                </MenuItem>
                {formulas.map((formula) => (
                  <MenuItem key={formula.id} value={formula.id}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                      title={formula.name}
                    >
                      {formula.name}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Box>
          <Box
            sx={{
              mb: 4,
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: `0 14px 32px ${colors.primary[900]}40`,
              border: `1px solid ${colors.primary[200]}30`,
              backgroundColor: colors.primary[500],
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: { xs: 2.5, md: 3.5 },
                py: 2.5,
                background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: 3,
                    backgroundColor: colors.primary[500],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.grey[100],
                    boxShadow: `0 10px 26px ${colors.primary[900]}55`,
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 30 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      color: colors.grey[100],
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      fontSize: "1.1rem",
                    }}
                  >
                    {t('forms:formOverview')}
                  </Typography>
                  <Typography sx={{ color: colors.grey[200], opacity: 0.85 }}>
                    {t('forms:formOverviewDescriptionCreate')}
                  </Typography>
                </Box>
              </Box>

              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  flexWrap: "wrap",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                }}
              >
                <Chip
                  label={`${t('forms:fileLabel')} ${fileInfo?.name || "—"}`}
                  sx={{
                    backgroundColor: `${colors.primary[500]}aa`,
                    color: colors.grey[100],
                    borderRadius: 2,
                    fontWeight: 600,
                    px: 1,
                  }}
                />
                <Chip
                  label={`${t('forms:renderMode')} ${forPatients === "for patients" ? t('forms:willBeSentToPatients') : forPatients === "for me" ? t('forms:forMyOwnUse') : t('forms:notSpecified')}`}
                  sx={{
                    backgroundColor: `${colors.primary[500]}aa`,
                    color: colors.grey[100],
                    borderRadius: 2,
                    fontWeight: 600,
                    px: 1,
                  }}
                />
              </Stack>
            </Box>

            <Box
              sx={{
                px: { xs: 2.5, md: 3.5 },
                py: 3,
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {/* Global Scoring Exclusion Switch */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: colors.primary[600],
                  border: `2px solid ${excludeAllFromScoring ? colors.redAccent[500] : colors.blueAccent[500]}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "all 0.3s ease",
                  boxShadow: excludeAllFromScoring 
                    ? `0 4px 12px ${colors.redAccent[500]}40`
                    : `0 4px 12px ${colors.blueAccent[500]}40`,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: colors.grey[100],
                      fontWeight: 700,
                      mb: 0.5,
                    }}
                  >
                    {excludeAllFromScoring 
                      ? t('forms:allQuestionsExcludedFromScoring')
                      : t('forms:includeQuestionsInScoring')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.grey[300],
                      display: "block",
                    }}
                  >
                    {excludeAllFromScoring
                      ? t('forms:noneOfQuestionsIncluded')
                      : t('forms:questionsCanBeIndividually')}
                  </Typography>
                </Box>
                <Switch
                  checked={excludeAllFromScoring}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setExcludeAllFromScoring(newValue);
                    // Update all questions' hasImportance based on global setting
                    const updatedFields = fields.map((field) => ({
                      ...field,
                      hasImportance: !newValue, // If excludeAllFromScoring is true, all should be false
                    }));
                    setFields(updatedFields);
                  }}
                  sx={{
                    ml: 2,
                    "& .MuiSwitch-switchBase": {
                      color: excludeAllFromScoring ? colors.redAccent[400] : colors.grey[400],
                    },
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: colors.redAccent[500],
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: colors.redAccent[500],
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: colors.grey[600],
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {t('forms:formTitle')}
              </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t('forms:formTitleExample')}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={handleTitleBlur}
                  InputProps={{
                    sx: {
                      borderRadius: 3,
                      backgroundColor: colors.primary[500],
                      fontSize: "1.7rem",
                      fontWeight: 700,
                      color: colors.grey[100],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}44`,
                      },
                      "&:hover fieldset": {
                        borderColor: colors.greenAccent[400],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.greenAccent[500],
                        boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      color: colors.grey[400],
                      "&.Mui-focused": {
                        color: colors.greenAccent[300],
                      },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ display: "block", textAlign: "right", mt: 0.5, color: colors.grey[400] }}
                >
                  {title?.length || 0} {t('forms:characters')}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {t('forms:formDescription')}
              </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={3}
                  placeholder={t('forms:formDescriptionPlaceholder')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  InputProps={{
                    sx: {
                      borderRadius: 3,
                      backgroundColor: colors.primary[500],
                      color: colors.grey[200],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}33`,
                      },
                      "&:hover fieldset": {
                        borderColor: colors.blueAccent[300],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.blueAccent[200],
                        boxShadow: `0 0 0 3px ${colors.blueAccent[400]}33`,
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      color: colors.grey[400],
                      "&.Mui-focused": {
                        color: colors.blueAccent[300],
                      },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ display: "block", textAlign: "right", mt: 0.5, color: colors.grey[400] }}
                >
                  {description?.length || 0} {t('forms:characters')}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box>
            {fields.map((field, index) => {
              const meta = questionMeta[field.type] || questionMeta.text;

              return (
                <Box key={index} sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      borderRadius: 4,
                      overflow: "hidden",
                      boxShadow: `0 12px 30px ${colors.primary[900]}40`,
                      border: `1px solid ${colors.primary[200]}30`,
                      backgroundColor: colors.primary[500],
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        px: { xs: 2.5, md: 3.5 },
                        py: 2.5,
                        background: meta.gradient,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 3,
                            backgroundColor: colors.primary[500],
                            color: colors.grey[100],
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: `0 8px 20px ${colors.primary[900]}55`,
                          }}
                        >
                          {meta.icon}
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: colors.grey[100],
                              letterSpacing: 0.4,
                            }}
                          >
                            {t('forms:questionNumber', { index: index + 1 })} {meta.title}
                          </Typography>
                          {meta.subtitle && (
                            <Typography variant="body2" sx={{ color: colors.grey[200], opacity: 0.85 }}>
                              {meta.subtitle}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Button
                          onClick={() => handleDuplicateQuestion(index)}
                          startIcon={<ContentCopyIcon />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            px: 2.5,
                            py: 1,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
                            color: "white",
                            boxShadow: `0 6px 16px ${colors.blueAccent[500]}55`,
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: `0 8px 20px ${colors.blueAccent[500]}70`,
                            },
                          }}
                        >
                          {t('forms:copyQuestion')}
                        </Button>

                        <Button
                          onClick={() => handleDeleteQuestion(index)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            px: 2.5,
                            py: 1,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${colors.redAccent[500]} 0%, ${colors.redAccent[600]} 100%)`,
                            color: "white",
                            boxShadow: `0 6px 16px ${colors.redAccent[500]}40`,
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: `0 8px 24px ${colors.redAccent[500]}60`,
                            },
                          }}
                        >
                          {t('forms:deleteQuestion')}
                        </Button>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        px: { xs: 2.5, md: 3.5 },
                        py: 3,
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                      }}
                    >
                      <TextField
                        fullWidth
                        variant="outlined"
                        label={t('forms:enterQuestionText')}
                        placeholder={t('forms:describePatientSymptoms')}
                        sx={{
                          maxWidth: 720,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            backgroundColor: colors.primary[500],
                            '& fieldset': {
                              borderColor: `${colors.primary[200]}44`,
                            },
                            '&:hover fieldset': {
                              borderColor: colors.greenAccent[400],
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: colors.greenAccent[500],
                              boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: colors.grey[300],
                          },
                        }}
                        value={field.question}
                        onChange={(e) => handleQuestionChange(index, e.target.value)}
                      />

                      {field.type === "text" && (
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            backgroundColor: colors.primary[600],
                            border: `1px solid ${colors.primary[200]}22`,
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ color: colors.grey[300], mb: 1 }}>
                            {t('forms:preview')}
                          </Typography>
                          <TextField
                            fullWidth
                            variant="outlined"
                            placeholder={t('forms:patientResponseWillAppearHere')}
                            disabled
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                backgroundColor: colors.primary[500],
                                color: colors.grey[400],
                                '& fieldset': {
                                  borderColor: `${colors.primary[200]}33`,
                                },
                              },
                            }}
                          />
                        </Box>
                      )}

                      {field.type === "multiple_choice" && (() => {
                        const includeInScoreForMc =
                          !excludeAllFromScoring &&
                          (field.hasImportance !== undefined ? field.hasImportance : true);
                        return (
                        <Box>
                          {/* Importance Toggle Switch */}
                          <Box
                            sx={{
                              mb: 3,
                              p: 2,
                              borderRadius: 2,
                              backgroundColor: colors.primary[600],
                              border: `1px solid ${colors.primary[200]}33`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color: colors.grey[200],
                                  fontWeight: 600,
                                  mb: 0.5,
                                }}
                              >
                                {t('forms:hasImportance')}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: colors.grey[400],
                                }}
                              >
                                {excludeAllFromScoring
                                  ? t('forms:allQuestionsExcludedGlobal')
                                  : (field.hasImportance !== undefined ? field.hasImportance : true)
                                  ? t('forms:thisQuestionWillBeIncluded')
                                  : t('forms:thisQuestionWillNotBeIncluded')}
                              </Typography>
                            </Box>
                            <Switch
                              checked={excludeAllFromScoring ? false : (field.hasImportance !== undefined ? field.hasImportance : true)}
                              onChange={(e) => {
                                if (!excludeAllFromScoring) {
                                  const updatedFields = [...fields];
                                  updatedFields[index].hasImportance = e.target.checked;
                                  setFields(updatedFields);
                                }
                              }}
                              disabled={excludeAllFromScoring}
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: colors.greenAccent[500],
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                  backgroundColor: colors.greenAccent[500],
                                },
                                "& .Mui-disabled": {
                                  opacity: 0.5,
                                },
                              }}
                            />
                          </Box>
                          <FormControl fullWidth>
                            <RadioGroup>
                              {field.options.map((option, optionIdx) => (
                                <FormControlLabel
                                  key={optionIdx}
                                  value={option.label}
                                  control={<Radio disabled />}
                                  label={
                                    <Box
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                          xs: "1fr",
                                          md: includeInScoreForMc ? "1fr 160px auto" : "1fr auto",
                                        },
                                        gap: 2,
                                        alignItems: "center",
                                        backgroundColor: `${colors.primary[600]}AA`,
                                        borderRadius: 3,
                                        border: `1px solid ${colors.primary[200]}33`,
                                        boxShadow: `0 6px 16px ${colors.primary[900]}33`,
                                        px: 2,
                                        py: 1.5,
                                      }}
                                    >
                                      <MuiTextField
                                        value={option.label}
                                        onChange={(e) =>
                                          handleOptionChange(index, optionIdx, {
                                            ...option,
                                            label: e.target.value,
                                          })
                                        }
                                        label={t('forms:optionNumber', { index: optionIdx + 1 })}
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: colors.primary[500],
                                            '& fieldset': {
                                              borderColor: `${colors.primary[200]}33`,
                                            },
                                            '&:hover fieldset': {
                                              borderColor: colors.greenAccent[400],
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: colors.greenAccent[500],
                                              boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                                            },
                                          },
                                        }}
                                      />

                                      {includeInScoreForMc && (
                                      <MuiTextField
                                        type="number"
                                        value={
                                          option.importance !== null
                                            ? option.importance
                                            : ""
                                        }
                                        onChange={(e) =>
                                          handleOptionChange(index, optionIdx, {
                                            ...option,
                                            importance:
                                              e.target.value === ""
                                                ? null
                                                : Number(e.target.value),
                                          })
                                        }
                                        label={t('forms:importance')}
                                        variant="outlined"
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: colors.primary[500],
                                            '& fieldset': {
                                              borderColor: `${colors.primary[200]}33`,
                                            },
                                            '&:hover fieldset': {
                                              borderColor: colors.blueAccent[400],
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: colors.blueAccent[300],
                                              boxShadow: `0 0 0 3px ${colors.blueAccent[300]}33`,
                                            },
                                          },
                                          '& .MuiInputBase-input': {
                                            textAlign: "center",
                                            color: colors.grey[100],
                                            fontWeight: 600,
                                          },
                                        }}
                                      />
                                      )}
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          ml: { xs: 0, md: 1 },
                                          justifySelf: { xs: "stretch", md: "end" },
                                          textTransform: "none",
                                          fontWeight: 600,
                                          borderRadius: 2,
                                          backgroundColor: colors.redAccent[600],
                                          color: "white",
                                          '&:hover': {
                                            backgroundColor: colors.redAccent[500],
                                          },
                                        }}
                                        onClick={() => {
                                          const updatedFields = [...fields];
                                          if (
                                            updatedFields[index].options.length > 2
                                          ) {
                                            updatedFields[index].options.splice(
                                              optionIdx,
                                              1
                                            );
                                          }
                                          setFields(updatedFields);
                                        }}
                                        >
                                        {t('forms:delete')}
                                      </Button>
                                    </Box>
                                  }
                                />
                              ))}
                            </RadioGroup>

                            <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
                              <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => handleAddOption(index)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  borderRadius: 2,
                                  px: 2.5,
                                  backgroundColor: colors.primary[500],
                                  color: colors.grey[100],
                                  '&:hover': {
                                    backgroundColor: colors.primary[400],
                                    borderColor: colors.greenAccent[400],
                                  },
                                }}
                              >
                                {t('forms:addOptionWithIcon')}
                              </Button>
                              {includeInScoreForMc && (
                              <Tooltip title="Click to make the option values and importance levels the same.">
                                <Button
                                  variant="outlined"
                                  color="secondary"
                                  onClick={() => handleMatchLabelImportance(index)}
                                  sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    backgroundColor: colors.blueAccent[600],
                                    color: "white",
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    px: 2.5,
                                    '&:hover': {
                                      backgroundColor: colors.blueAccent[500],
                                    },
                                  }}
                                >
                                  {t('forms:matchLabelToImportance')}
                                </Button>
                              </Tooltip>
                              )}
                            </Box>
                          </FormControl>
                        </Box>
                        );
                      })()}
                  {field.type === "table" && (
                    <Box sx={{ margin: 3 }}>
                      {/* Importance Toggle Switch */}
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: colors.primary[600],
                          border: `1px solid ${colors.primary[200]}33`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: colors.grey[200],
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            {t('forms:hasImportance')}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.grey[400],
                            }}
                          >
                            {excludeAllFromScoring
                              ? t('forms:allQuestionsExcludedGlobal')
                              : field.hasImportance !== undefined && field.hasImportance
                              ? t('forms:thisQuestionWillBeIncluded')
                              : t('forms:thisQuestionWillNotBeIncluded')}
                          </Typography>
                        </Box>
                        <Switch
                          checked={excludeAllFromScoring ? false : (field.hasImportance !== undefined ? field.hasImportance : true)}
                          onChange={(e) => {
                            if (!excludeAllFromScoring) {
                              const updatedFields = [...fields];
                              updatedFields[index].hasImportance = e.target.checked;
                              setFields(updatedFields);
                            }
                          }}
                          disabled={excludeAllFromScoring}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: colors.greenAccent[500],
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: colors.greenAccent[500],
                            },
                            "& .Mui-disabled": {
                              opacity: 0.5,
                            },
                          }}
                        />
                      </Box>
                      <Box sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 2,
                        mb: 3
                      }}>
                        <TableChartIcon sx={{ 
                          fontSize: 28,
                          color: colors.greenAccent[500]
                        }} />
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            fontWeight: "bold",
                            background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent"
                          }}
                        >
                          {t('forms:tableQuestion')}
                        </Typography>
                        <Box sx={{ 
                          ml: "auto",
                          display: "flex",
                          gap: 1,
                          alignItems: "center"
                        }}>
                          <Box sx={{ 
                            px: 2, 
                            py: 0.5, 
                            borderRadius: 2,
                            backgroundColor: colors.blueAccent[700],
                            border: `1px solid ${colors.blueAccent[500]}`
                          }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                              📝 {t('forms:rowsLabel')} {field.tableRows}
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            px: 2, 
                            py: 0.5, 
                            borderRadius: 2,
                            backgroundColor: colors.greenAccent[700],
                            border: `1px solid ${colors.greenAccent[500]}`
                          }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                              📊 {t('forms:columnsLabel')} {field.tableCols}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ 
                        width: "100%",
                        maxWidth: "100%",
                        overflowX: "hidden",
                        borderRadius: 2,
                        border: `3px solid ${colors.greenAccent[700]}`,
                        boxShadow: `0 4px 16px rgba(0, 0, 0, 0.2)`,
                        background: colors.primary[500]
                      }}>
                        <Table
                          size="small"
                          sx={{ tableLayout: "fixed", width: "100%" }}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell sx={tableCellShrinkBase(1 + (field.tableHeaders?.length || field.tableCols || 1), {
                                background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`,
                                fontWeight: "bold",
                                border: `2px solid ${colors.blueAccent[600]}`,
                                color: colors.grey[100],
                                fontSize: "0.85rem",
                                textAlign: "center",
                                px: 0.75,
                                py: 1,
                              })}>
                                ❓ {t('forms:questionsLabel')}
                              </TableCell>
                              {field.tableHeaders.map((header, headerIdx) => (
                                <TableCell 
                                  key={headerIdx}
                                  sx={tableCellShrinkBase(1 + (field.tableHeaders?.length || field.tableCols || 1), {
                                    background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`,
                                    fontWeight: "bold",
                                    border: `2px solid ${colors.greenAccent[600]}`,
                                    px: 0.75,
                                    py: 1,
                                  })}
                                >
                                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                                    <MuiTextField
                                      value={header}
                                      onChange={(e) => {
                                        const updatedFields = [...fields];
                                        updatedFields[index].tableHeaders[headerIdx] = e.target.value;
                                        setFields(updatedFields);
                                      }}
                                      variant="standard"
                                      size="small"
                                      fullWidth
                                      sx={{ 
                                        minWidth: 0,
                                        "& .MuiInput-root": { 
                                          fontSize: "0.9rem",
                                          fontWeight: "bold",
                                          color: colors.grey[100]
                                        },
                                        "& .MuiInput-root:before": {
                                          borderBottomColor: colors.greenAccent[400]
                                        },
                                        "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                                          borderBottomColor: colors.greenAccent[300]
                                        },
                                        "& .MuiInput-root:after": {
                                          borderBottomColor: colors.greenAccent[200]
                                        }
                                      }}
                                      InputProps={{
                                        style: { color: colors.grey[100] }
                                      }}
                                    />
                                    <Box sx={{ 
                                      display: "flex", 
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexWrap: "wrap",
                                      gap: 0.75,
                                      backgroundColor: colors.primary[600],
                                      borderRadius: 2,
                                      p: 0.75
                                    }}>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const updatedFields = [...fields];
                                          const currentValue = updatedFields[index].tableHeaderImportance?.[headerIdx] || 0;
                                          if (!updatedFields[index].tableHeaderImportance) {
                                            updatedFields[index].tableHeaderImportance = new Array(field.tableHeaders.length).fill(0);
                                          }
                                          updatedFields[index].tableHeaderImportance[headerIdx] = Math.max(0, currentValue - 1);
                                          setFields(updatedFields);
                                        }}
                                        sx={{
                                          backgroundColor: colors.redAccent[700],
                                          color: "white",
                                          width: 24,
                                          height: 24,
                                          "&:hover": {
                                            backgroundColor: colors.redAccent[600],
                                            transform: "scale(1.1)"
                                          },
                                          transition: "all 0.2s"
                                        }}
                                      >
                                        <RemoveIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                      <MuiTextField
                                        type="number"
                                        value={field.tableHeaderImportance?.[headerIdx] ?? 0}
                                        onChange={(e) => {
                                          const updatedFields = [...fields];
                                          if (!updatedFields[index].tableHeaderImportance) {
                                            updatedFields[index].tableHeaderImportance = new Array(field.tableHeaders.length).fill(0);
                                          }
                                          updatedFields[index].tableHeaderImportance[headerIdx] = e.target.value === "" ? 0 : Number(e.target.value);
                                          setFields(updatedFields);
                                        }}
                                        placeholder="0"
                                        variant="standard"
                                        size="small"
                                        sx={{ 
                                          width: 60,
                                          "& .MuiInput-root": { 
                                            fontSize: "0.9rem",
                                            fontWeight: "bold",
                                            color: colors.grey[100],
                                            textAlign: "center"
                                          },
                                          "& .MuiInput-root:before": {
                                            borderBottomColor: colors.greenAccent[400]
                                          },
                                          "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                                            borderBottomColor: colors.greenAccent[300]
                                          },
                                          "& .MuiInput-root:after": {
                                            borderBottomColor: colors.greenAccent[200]
                                          }
                                        }}
                                        inputProps={{
                                          style: { textAlign: "center", color: colors.grey[100] },
                                          min: 0
                                        }}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const updatedFields = [...fields];
                                          const currentValue = updatedFields[index].tableHeaderImportance?.[headerIdx] || 0;
                                          if (!updatedFields[index].tableHeaderImportance) {
                                            updatedFields[index].tableHeaderImportance = new Array(field.tableHeaders.length).fill(0);
                                          }
                                          updatedFields[index].tableHeaderImportance[headerIdx] = currentValue + 1;
                                          setFields(updatedFields);
                                        }}
                                        sx={{
                                          backgroundColor: colors.greenAccent[700],
                                          color: "white",
                                          width: 24,
                                          height: 24,
                                          "&:hover": {
                                            backgroundColor: colors.greenAccent[600],
                                            transform: "scale(1.1)"
                                          },
                                          transition: "all 0.2s"
                                        }}
                                      >
                                        <AddIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Box>
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        color: colors.grey[400],
                                        fontSize: "0.7rem",
                                        fontStyle: "italic",
                                        textAlign: "center"
                                      }}
                                    >
                                      💯{" "}
                                      {t("forms:scoreLabel", {
                                        score: field.tableHeaderImportance?.[headerIdx] ?? 0,
                                      })}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {field.tableQuestions.map((question, questionIdx) => (
                              <TableRow 
                                key={questionIdx}
                                sx={{
                                  "&:hover": {
                                    backgroundColor: colors.primary[600]
                                  },
                                  transition: "background-color 0.2s"
                                }}
                              >
                                <TableCell sx={tableCellShrinkBase(1 + (field.tableHeaders?.length || field.tableCols || 1), {
                                  backgroundColor: colors.blueAccent[900],
                                  border: `1px solid ${colors.blueAccent[700]}`,
                                  px: 0.75,
                                  py: 0.75,
                                })}>
                                  <MuiTextField
                                    value={question}
                                    onChange={(e) => {
                                      const updatedFields = [...fields];
                                      updatedFields[index].tableQuestions[questionIdx] = e.target.value;
                                      setFields(updatedFields);
                                    }}
                                    variant="standard"
                                    size="small"
                                    fullWidth
                                    sx={{ 
                                      minWidth: 0,
                                      "& .MuiInput-root": { 
                                        fontSize: "0.85rem",
                                        color: colors.grey[100]
                                      },
                                      "& .MuiInput-root:before": {
                                        borderBottomColor: colors.blueAccent[400]
                                      },
                                      "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                                        borderBottomColor: colors.blueAccent[300]
                                      },
                                      "& .MuiInput-root:after": {
                                        borderBottomColor: colors.blueAccent[200]
                                      }
                                    }}
                                    InputProps={{
                                      style: { color: colors.grey[100] }
                                    }}
                                  />
                                </TableCell>
                                {field.tableHeaders.map((header, headerIdx) => (
                                  <TableCell 
                                    key={headerIdx}
                                    sx={tableCellShrinkBase(1 + (field.tableHeaders?.length || field.tableCols || 1), {
                                      border: `1px solid ${colors.greenAccent[700]}`,
                                      textAlign: "center",
                                      backgroundColor: colors.primary[600],
                                      px: 0.5,
                                      py: 0.5,
                                      "&:hover": {
                                        backgroundColor: colors.primary[500]
                                      },
                                      transition: "background-color 0.2s"
                                    })}
                                  >
                                    <Radio 
                                      disabled 
                                      size="small"
                                      sx={{
                                        color: colors.greenAccent[600],
                                        "&.Mui-disabled": {
                                          color: colors.greenAccent[700]
                                        }
                                      }}
                                    />
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                      <Box sx={{ 
                        mt: 2,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: colors.primary[600],
                        border: `1px solid ${colors.blueAccent[700]}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                      }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: colors.blueAccent[400],
                            fontStyle: "italic"
                          }}
                        >
                          {t('forms:tableQuestionTip')}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {field.type === "image" && (
                    <Box sx={{ margin: 3 }}>
                      <Box sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 2,
                        mb: 3
                      }}>
                        <ImageIcon sx={{ 
                          fontSize: 28,
                          color: colors.greenAccent[500]
                        }} />
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            fontWeight: "bold",
                            background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent"
                          }}
                        >
                          {t('forms:imageQuestionICRS')}
                        </Typography>
                        <Box sx={{ 
                          ml: "auto",
                          px: 2, 
                          py: 0.5, 
                          borderRadius: 2,
                          backgroundColor: colors.greenAccent[700],
                          border: `1px solid ${colors.greenAccent[500]}`
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                            🗺️ {t('forms:areasLabel')} {field.areas?.length || 0}
                          </Typography>
                        </Box>
                      </Box>
                      <ImageQuestion
                        questionData={field}
                        onQuestionDataChange={(updatedData) => 
                          handleImageQuestionDataChange(index, updatedData)
                        }
                      />
                    </Box>
                  )}

                  {field.type === "rating_scale" && (
                    <Box sx={{ px: { xs: 0, md: 0 }, py: 1 }}>
                      <RatingScaleQuestionEditor
                        field={field}
                        colors={colors}
                        excludeAllFromScoring={excludeAllFromScoring}
                        t={t}
                        onFieldChange={(updates) => {
                          const next = [...fields];
                          next[index] = { ...next[index], ...updates };
                          setFields(next);
                        }}
                      />
                    </Box>
                  )}

                </Box>
              </Box>
            </Box>
          );
        })}

            {fields.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  mt: 3,
                }}
              >
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    px: 3.5,
                    py: 1.2,
                    borderRadius: 2.5,
                    background: forPatients === "for patients"
                      ? `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.greenAccent[600]} 100%)`
                      : `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
                    color: "white",
                    boxShadow: forPatients === "for patients"
                      ? `0 8px 22px ${colors.greenAccent[500]}45`
                      : `0 8px 22px ${colors.blueAccent[500]}45`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: forPatients === "for patients"
                        ? `0 10px 26px ${colors.greenAccent[500]}60`
                        : `0 10px 26px ${colors.blueAccent[500]}60`,
                    },
                  }}
                >
                  {isSubmitting ? t('forms:creating') : t('forms:createForm')}
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ marginTop: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: colors.grey[300], mb: 1.5, letterSpacing: 0.5, textTransform: "uppercase" }}
            >
              {t('forms:addQuestions')}
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Button
                variant="contained"
                onClick={handleAddTextQuestion}
                sx={{
                  minWidth: 200,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[400]} 100%)`,
                  boxShadow: `0 4px 16px ${colors.blueAccent[600]}40`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${colors.blueAccent[600]}60`,
                  },
                }}
              >
                {t('forms:addTextQuestion')}
              </Button>
              <Button
                variant="contained"
                onClick={handleAddMultipleChoiceQuestion}
                sx={{
                  minWidth: 200,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
                  boxShadow: `0 4px 16px ${colors.greenAccent[600]}40`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${colors.greenAccent[600]}60`,
                  },
                }}
              >
                {t('forms:addMultipleChoice')}
              </Button>
              <Button
                variant="contained"
                onClick={handleAddTableQuestion}
                startIcon={<TableChartIcon />}
                sx={{
                  minWidth: 200,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                  boxShadow: `0 4px 16px ${colors.greenAccent[600]}40`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${colors.greenAccent[600]}60`,
                  },
                }}
              >
                {t('forms:addTableQuestion')}
              </Button>
              <Button
                variant="contained"
                onClick={handleAddImageQuestion}
                startIcon={<ImageIcon />}
                sx={{
                  minWidth: 220,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                  boxShadow: `0 4px 16px ${colors.greenAccent[500]}40`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${colors.greenAccent[500]}60`,
                  },
                }}
              >
                {t('forms:addImageQuestionICRS')}
              </Button>
              <Button
                variant="contained"
                onClick={handleAddRatingScaleQuestion}
                startIcon={<LinearScaleIcon />}
                sx={{
                  minWidth: 220,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
                  boxShadow: `0 4px 16px ${colors.redAccent[600]}40`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${colors.greenAccent[600]}60`,
                  },
                }}
              >
                {t('forms:addRatingScaleQuestion')}
              </Button>
            </Stack>
          </Box>
        </Box>
      </form>
      <WarningSnackbar
        open={snackbarState}
        autoHideDuration={3000}
        onClose={handleCloseSnackBar}
        message={t('forms:formCreatedSuccessfully')}
        severity={"success"}
      />
      <WarningSnackbar
        open={errorSnackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseErrorSnackbar}
        message={errorSnackbar.message}
        severity="error"
      />
      <WarningSnackbar
        open={copyDraftSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseCopyDraftSnackbar}
        message={copyDraftSnackbar.message}
        severity="info"
      />

      {/* Confirmation Dialog for No Formula */}
      <AlertDialog
        open={noFormulaConfirmDialogOpen}
        onClose={() => setNoFormulaConfirmDialogOpen(false)}
        onConfirm={() => {
          setNoFormulaConfirmDialogOpen(false);
          createFormWithData();
        }}
        type="warning"
        title={t('forms:noScoringFormulaSelected')}
        message={t('forms:noScoringFormulaMessage')}
        description={t('forms:noScoringFormulaDescription')}
        confirmText={t('forms:yesCreateWithoutFormula')}
        cancelText={t('forms:cancel')}
        showCancel={true}
      />

      {/* Table Question Creation Modal */}
      <Dialog 
        open={tableModalOpen} 
        onClose={() => setTableModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: colors.primary[400],
            borderRadius: 3,
            boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3)`,
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `2px solid ${colors.greenAccent[400]}`,
          pb: 2
        }}>
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 2,
            background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent"
          }}>
            <TableChartIcon sx={{ 
              fontSize: 32,
              color: colors.greenAccent[500]
            }} />
            <Typography variant="h4" fontWeight="bold">
              {t('forms:createTableQuestion')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mt: 1 }}>
            {/* Table Size Controls */}
            <Box sx={{ 
              mb: 4, 
              p: 3, 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${colors.primary[300]} 0%, ${colors.primary[400]} 100%)`,
              border: `2px solid ${colors.greenAccent[700]}`,
              boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15)`
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3, 
                  color: colors.greenAccent[400],
                  fontWeight: "bold"
                }}
              >
                {t('forms:tableSizeConfiguration')}
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: "column",
                    alignItems: "center",
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: colors.primary[500],
                    border: `1px solid ${colors.blueAccent[700]}`
                  }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        mb: 2, 
                        fontWeight: "bold",
                        color: colors.blueAccent[400]
                      }}
                    >
                      {t('forms:rowsQuestions')}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <IconButton 
                        onClick={() => handleTableRowsChange(tableRows - 1)}
                        disabled={tableRows <= 2}
                        size="medium"
                        sx={{ 
                          backgroundColor: colors.redAccent[600],
                          color: "white",
                          "&:hover": { 
                            backgroundColor: colors.redAccent[700],
                            transform: "scale(1.1)"
                          },
                          "&:disabled": {
                            backgroundColor: colors.grey[700],
                            color: colors.grey[500]
                          },
                          transition: "all 0.2s"
                        }}
                      >
                        <RemoveIcon />
                      </IconButton>
                      <Box sx={{ 
                        minWidth: 60, 
                        textAlign: "center",
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: colors.blueAccent[800],
                        border: `2px solid ${colors.blueAccent[600]}`
                      }}>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: "bold",
                            color: colors.blueAccent[300]
                          }}
                        >
                          {tableRows}
                        </Typography>
                      </Box>
                      <IconButton 
                        onClick={() => handleTableRowsChange(tableRows + 1)}
                        disabled={tableRows >= 20}
                        size="medium"
                        sx={{ 
                          backgroundColor: colors.greenAccent[600],
                          color: "white",
                          "&:hover": { 
                            backgroundColor: colors.greenAccent[700],
                            transform: "scale(1.1)"
                          },
                          "&:disabled": {
                            backgroundColor: colors.grey[700],
                            color: colors.grey[500]
                          },
                          transition: "all 0.2s"
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        mt: 1, 
                        color: colors.grey[400],
                        fontSize: "0.75rem"
                      }}
                    >
                      {t('forms:min')} 2, {t('forms:max')} 20
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: "column",
                    alignItems: "center",
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: colors.primary[500],
                    border: `1px solid ${colors.greenAccent[700]}`
                  }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        mb: 2, 
                        fontWeight: "bold",
                        color: colors.greenAccent[400]
                      }}
                    >
                      {t('forms:columnsOptions')}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <IconButton 
                        onClick={() => handleTableColsChange(tableCols - 1)}
                        disabled={tableCols <= 2}
                        size="medium"
                        sx={{ 
                          backgroundColor: colors.redAccent[600],
                          color: "white",
                          "&:hover": { 
                            backgroundColor: colors.redAccent[700],
                            transform: "scale(1.1)"
                          },
                          "&:disabled": {
                            backgroundColor: colors.grey[700],
                            color: colors.grey[500]
                          },
                          transition: "all 0.2s"
                        }}
                      >
                        <RemoveIcon />
                      </IconButton>
                      <Box sx={{ 
                        minWidth: 60, 
                        textAlign: "center",
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: colors.greenAccent[800],
                        border: `2px solid ${colors.greenAccent[600]}`
                      }}>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: "bold",
                            color: colors.greenAccent[300]
                          }}
                        >
                          {tableCols}
                        </Typography>
                      </Box>
                      <IconButton 
                        onClick={() => handleTableColsChange(tableCols + 1)}
                        disabled={tableCols >= 15}
                        size="medium"
                        sx={{ 
                          backgroundColor: colors.greenAccent[600],
                          color: "white",
                          "&:hover": { 
                            backgroundColor: colors.greenAccent[700],
                            transform: "scale(1.1)"
                          },
                          "&:disabled": {
                            backgroundColor: colors.grey[700],
                            color: colors.grey[500]
                          },
                          transition: "all 0.2s"
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        mt: 1, 
                        color: colors.grey[400],
                        fontSize: "0.75rem"
                      }}
                    >
                      {t('forms:min')} 2, {t('forms:max')} 15
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Table Preview */}
            <Box sx={{ 
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: colors.blueAccent[400],
                  fontWeight: "bold"
                }}
              >
                {t('forms:tablePreview')}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: colors.grey[400],
                  ml: 1
                }}
              >
                {t('forms:editHeadersAndQuestions')}
              </Typography>
            </Box>
            <Box sx={{ 
              width: "100%",
              maxWidth: "100%",
              overflowX: "hidden",
              borderRadius: 2,
              border: `3px solid ${colors.greenAccent[700]}`,
              boxShadow: `0 4px 16px rgba(0, 0, 0, 0.2)`,
              background: colors.primary[500]
            }}>
              <Table
                size="small"
                sx={{ tableLayout: "fixed", width: "100%" }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableCellShrinkBase(1 + (tableHeaders?.length || tableCols || 1), {
                      background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`,
                      fontWeight: "bold",
                      border: `2px solid ${colors.blueAccent[600]}`,
                      color: colors.grey[100],
                      fontSize: "0.85rem",
                      textAlign: "center",
                      px: 0.75,
                      py: 1,
                    })}>
                      ❓ {t('forms:questionsLabel')}
                    </TableCell>
                    {tableHeaders.map((header, headerIdx) => (
                      <TableCell 
                        key={headerIdx}
                        sx={tableCellShrinkBase(1 + (tableHeaders?.length || tableCols || 1), {
                          background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`,
                          fontWeight: "bold",
                          border: `2px solid ${colors.greenAccent[600]}`,
                          px: 0.75,
                          py: 1,
                        })}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                          <MuiTextField
                            value={header}
                            onChange={(e) => handleTableHeaderChange(headerIdx, e.target.value)}
                            placeholder={t('forms:optionName')}
                            variant="standard"
                            size="small"
                            fullWidth
                            sx={{ 
                              minWidth: 0,
                              "& .MuiInput-root": { 
                                fontSize: "0.9rem",
                                fontWeight: "bold",
                                color: colors.grey[100]
                              },
                              "& .MuiInput-root:before": {
                                borderBottomColor: colors.greenAccent[400]
                              },
                              "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                                borderBottomColor: colors.greenAccent[300]
                              },
                              "& .MuiInput-root:after": {
                                borderBottomColor: colors.greenAccent[200]
                              }
                            }}
                            InputProps={{
                              style: { color: colors.grey[100] }
                            }}
                          />
                          
                          <Box sx={{ 
                            display: "flex", 
                            alignItems: "center", 
                            justifyContent: "center",
                            flexWrap: "wrap",
                            gap: 0.75,
                            backgroundColor: colors.primary[600],
                            borderRadius: 2,
                            p: 0.75
                          }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const currentValue = tableHeaderImportance[headerIdx];
                                handleTableHeaderImportanceChange(headerIdx, Math.max(0, currentValue - 1));
                              }}
                              sx={{
                                backgroundColor: colors.redAccent[700],
                                color: "white",
                                width: 24,
                                height: 24,
                                "&:hover": {
                                  backgroundColor: colors.redAccent[600],
                                  transform: "scale(1.1)"
                                },
                                transition: "all 0.2s"
                              }}
                            >
                              <RemoveIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            
                            <MuiTextField
                              type="text"
                              autoComplete="off"
                              value={String(tableHeaderImportance[headerIdx] ?? 0)}
                              onChange={(e) =>
                                handleTableHeaderImportanceChange(headerIdx, e.target.value)
                              }
                              onFocus={(e) => {
                                const v = e.target.value;
                                if (v === "0" || v === "") {
                                  e.target.select();
                                }
                              }}
                              placeholder="0"
                              variant="standard"
                              size="small"
                              sx={{ 
                                width: 60,
                                "& .MuiInput-root": { 
                                  fontSize: "0.9rem",
                                  fontWeight: "bold",
                                  color: colors.grey[100],
                                  textAlign: "center"
                                },
                                "& .MuiInput-root:before": {
                                  borderBottomColor: colors.greenAccent[400]
                                },
                                "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                                  borderBottomColor: colors.greenAccent[300]
                                },
                                "& .MuiInput-root:after": {
                                  borderBottomColor: colors.greenAccent[200]
                                }
                              }}
                              inputProps={{
                                style: { textAlign: "center", color: colors.grey[100] },
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                            />
                            
                            <IconButton
                              size="small"
                              onClick={() => {
                                const currentValue = tableHeaderImportance[headerIdx];
                                handleTableHeaderImportanceChange(headerIdx, currentValue + 1);
                              }}
                              sx={{
                                backgroundColor: colors.greenAccent[700],
                                color: "white",
                                width: 24,
                                height: 24,
                                "&:hover": {
                                  backgroundColor: colors.greenAccent[600],
                                  transform: "scale(1.1)"
                                },
                                transition: "all 0.2s"
                              }}
                            >
                              <AddIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                          
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: colors.grey[400],
                              fontSize: "0.7rem",
                              fontStyle: "italic",
                              textAlign: "center"
                            }}
                          >
                            💯{" "}
                            {t("forms:scoreLabel", {
                              score: tableHeaderImportance[headerIdx],
                            })}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableQuestions.map((question, questionIdx) => (
                    <TableRow 
                      key={questionIdx}
                      sx={{
                        "&:hover": {
                          backgroundColor: colors.primary[600]
                        },
                        transition: "background-color 0.2s"
                      }}
                    >
                      <TableCell sx={tableCellShrinkBase(1 + (tableHeaders?.length || tableCols || 1), {
                        backgroundColor: colors.blueAccent[900],
                        border: `1px solid ${colors.blueAccent[700]}`,
                        px: 0.75,
                        py: 0.75,
                      })}>
                        <MuiTextField
                          value={question}
                          onChange={(e) => handleTableQuestionChange(questionIdx, e.target.value)}
                          variant="standard"
                          size="small"
                          fullWidth
                          sx={{ 
                            minWidth: 0,
                            "& .MuiInput-root": { 
                              fontSize: "0.85rem",
                              color: colors.grey[100]
                            },
                            "& .MuiInput-root:before": {
                              borderBottomColor: colors.blueAccent[400]
                            },
                            "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                              borderBottomColor: colors.blueAccent[300]
                            },
                            "& .MuiInput-root:after": {
                              borderBottomColor: colors.blueAccent[200]
                            }
                          }}
                          InputProps={{
                            style: { color: colors.grey[100] }
                          }}
                        />
                      </TableCell>
                      {tableHeaders.map((header, headerIdx) => (
                        <TableCell 
                          key={headerIdx}
                          sx={tableCellShrinkBase(1 + (tableHeaders?.length || tableCols || 1), {
                            border: `1px solid ${colors.greenAccent[700]}`,
                            textAlign: "center",
                            backgroundColor: colors.primary[600],
                            px: 0.5,
                            py: 0.5,
                          })}
                        >
                          <Radio 
                            disabled 
                            size="small"
                            sx={{
                              color: colors.greenAccent[600],
                              "&.Mui-disabled": {
                                color: colors.greenAccent[700]
                              }
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          borderTop: `2px solid ${colors.primary[300]}`,
          gap: 2
        }}>
          <Button 
            onClick={() => setTableModalOpen(false)}
            variant="outlined"
            sx={{
              borderColor: colors.redAccent[500],
              color: colors.redAccent[400],
              fontWeight: "bold",
              px: 3,
              "&:hover": {
                borderColor: colors.redAccent[400],
                backgroundColor: colors.redAccent[900],
                transform: "scale(1.05)"
              },
              transition: "all 0.2s"
            }}
            >
              ✖ {t('forms:cancel')}
            </Button>
          <Button 
            onClick={handleCreateTableQuestion}
            variant="contained"
            sx={{ 
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
              color: "white",
              fontWeight: "bold",
              px: 4,
              boxShadow: `0 4px 12px rgba(0, 0, 0, 0.3)`,
              "&:hover": {
                background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.blueAccent[700]} 100%)`,
                transform: "scale(1.05)",
                boxShadow: `0 6px 16px rgba(0, 0, 0, 0.4)`
              },
              transition: "all 0.2s"
            }}
          >
            {t('forms:createTableQuestionButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Repeat Schedule Configuration Dialog */}
      <Dialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: colors.primary[400],
            borderRadius: 3,
            boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3)`,
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `2px solid ${colors.blueAccent[400]}`,
          pb: 2
        }}>
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 2,
            background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent"
          }}>
            <ScheduleIcon sx={{ 
              fontSize: 32,
              color: colors.blueAccent[400]
            }} />
            <Typography variant="h4" fontWeight="bold">
              {t('forms:configureRepeatScheduleTitle')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RepeatSchedulePicker
            repeatCount={repeatCount}
            value={repeatSchedule}
            onChange={setRepeatSchedule}
            error={false}
            helperText=""
          />
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          borderTop: `2px solid ${colors.primary[300]}`,
          gap: 2
        }}>
          <Button 
            onClick={() => setScheduleDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: colors.greenAccent[500],
              color: colors.greenAccent[400],
              fontWeight: "bold",
              px: 3,
              "&:hover": {
                borderColor: colors.greenAccent[400],
                backgroundColor: colors.greenAccent[900],
                transform: "scale(1.05)"
              },
              transition: "all 0.2s"
            }}
            >
              {t('forms:done')}
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
